import { QUEUES } from '@app/constants/permissions';
import { useOrganization } from '@app/selectors/organization';
import {
  InboxNotificationBehavior,
  useFullOrganizationData,
} from '@app/services/admin';
import { useOperatorsQuery } from '@app/services/operator';
import { useUserProperties } from '@app/services/user-properties';

import usePermissionVerify from './permission-verify';

export const useUnaddressed = (userId: string) => {
  const organization = useOrganization();
  const { data: fullOrgData, isLoading: isLoadingFullOrgData } =
    useFullOrganizationData(organization?._id || '');
  const { data: userProperties, isLoading: isLoadingUserProperties } =
    useUserProperties(userId || '');

  const { QUEUES: hasQueuesAccess } = usePermissionVerify([QUEUES]);

  const { data: operatorData, isLoading: isLoadingOperator } =
    useOperatorsQuery(
      {
        ids: userId ? [userId] : [],
      },
      {
        enabled: !!userId,
      },
    );

  const userBelongsToQueue =
    operatorData?.pages?.[0]?.operators?.[0]?.belongsToQueue ?? false;

  // Organization and user inbox behavior settings
  const orgBehavior =
    fullOrgData?.organizationSettings?.inboxBehavior?.behavior ??
    InboxNotificationBehavior.UNREAD;
  const isLockedByAdmin =
    fullOrgData?.organizationSettings?.inboxBehavior?.lockedByAdmin ?? true;
  const userBehavior = userProperties?.inboxBehavior;

  // Determine if user properties enable unaddressed:
  // - If user has no preference, use org default
  // - If locked by admin, use org default
  // - Otherwise, use user preference
  const isUserPropertiesUnaddressedEnabled =
    userBehavior === undefined || isLockedByAdmin
      ? orgBehavior === InboxNotificationBehavior.UNADDRESSED
      : userBehavior === InboxNotificationBehavior.UNADDRESSED;

  const userBelongsToQueueEnabled = hasQueuesAccess && userBelongsToQueue;

  const snoozeUnaddressedOrg =
    fullOrgData?.organizationSettings?.snoozeUnaddressedEnabled ?? false;

  const dismissUnaddressedOrg =
    fullOrgData?.organizationSettings?.dismissUnaddressedEnabled ?? false;

  const snoozeEnabled =
    (userBelongsToQueueEnabled || isUserPropertiesUnaddressedEnabled) &&
    snoozeUnaddressedOrg;

  const dismissEnabled =
    (userBelongsToQueueEnabled || isUserPropertiesUnaddressedEnabled) &&
    dismissUnaddressedOrg;

  return {
    unaddressedEnabled:
      userBelongsToQueueEnabled || isUserPropertiesUnaddressedEnabled,
    snoozeEnabled,
    dismissEnabled,
    /** True when any per-user notification suppression (snooze or dismiss) is
     *  available. Used by the live-update / cache plumbing to decide whether to
     *  check the case's snoozes + dismissals arrays — avoids threading two
     *  separate booleans through every function in the chain. */
    suppressionEnabled: snoozeEnabled || dismissEnabled,
    isLoading:
      isLoadingUserProperties || isLoadingFullOrgData || isLoadingOperator,
  };
};

export default useUnaddressed;
